/**
 * Login view — username/password form for dashboard authentication.
 *
 * Calls igloobot.auth.login via the gateway RPC and returns the
 * auth result (token + user info) to the caller for state management.
 */

import { html, nothing } from "lit";
import type { GatewayBrowserClient } from "../gateway.ts";

export type LoginViewState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  lastError: string | null;
  basePath: string;
};

export type LoginResult = {
  ok: true;
  user: {
    id: string;
    username: string;
    firstName: string | null;
    lastName: string | null;
    globalRole: string;
  };
  token: string;
};

/**
 * Render the full-page login screen.
 *
 * @param state  Minimal gateway connection state
 * @param onLogin  Callback when login succeeds
 */
export function renderLogin(state: LoginViewState, onLogin: (result: LoginResult) => void) {
  let loginError: string | null = null;
  let loginLoading = false;

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const username = (form.querySelector("#login-username") as HTMLInputElement).value.trim();
    const password = (form.querySelector("#login-password") as HTMLInputElement).value;

    if (!username || !password) {
      // Show inline error
      const errEl = form.querySelector(".login-error") as HTMLElement | null;
      if (errEl) {
        errEl.textContent = "Username and password are required";
        errEl.style.display = "block";
      }
      return;
    }

    if (!state.client || !state.connected) {
      const errEl = form.querySelector(".login-error") as HTMLElement | null;
      if (errEl) {
        errEl.textContent = "Not connected to gateway. Please wait...";
        errEl.style.display = "block";
      }
      return;
    }

    // Disable button
    const btn = form.querySelector("button[type=submit]") as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = "Signing in...";
    const errEl = form.querySelector(".login-error") as HTMLElement | null;
    if (errEl) {
      errEl.style.display = "none";
    }

    try {
      const result = (await state.client.request("igloobot.auth.login", {
        username,
        password,
      })) as { ok?: boolean; error?: string; user?: LoginResult["user"]; token?: string };

      if (result?.ok && result.user && result.token) {
        onLogin({
          ok: true,
          user: result.user,
          token: result.token,
        });
      } else {
        if (errEl) {
          errEl.textContent = result?.error ?? "Invalid credentials";
          errEl.style.display = "block";
        }
        btn.disabled = false;
        btn.textContent = "Sign In";
      }
    } catch (err) {
      if (errEl) {
        errEl.textContent = `Login failed: ${String(err)}`;
        errEl.style.display = "block";
      }
      btn.disabled = false;
      btn.textContent = "Sign In";
    }
  };

  const basePath = state.basePath || "";

  return html`
    <div class="login-page">
      <div class="login-card">
        <div class="login-header">
          <div class="login-logo">
            <img src=${basePath ? `${basePath}/icon-512.png` : "/icon-512.png"} alt="" role="presentation" />
          </div>
          <h1 class="login-title">i-GlooBot</h1>
          <p class="login-subtitle">Agent Dashboard</p>
        </div>

        ${
          !state.connected
            ? html`
                <div class="login-connecting">Connecting to gateway...</div>
              `
            : nothing
        }

        <form class="login-form" @submit=${handleSubmit}>
          <div class="login-field">
            <label for="login-username">Username</label>
            <input
              id="login-username"
              type="text"
              autocomplete="username"
              placeholder="Enter username"
              ?disabled=${!state.connected}
            />
          </div>
          <div class="login-field">
            <label for="login-password">Password</label>
            <input
              id="login-password"
              type="password"
              autocomplete="current-password"
              placeholder="Enter password"
              ?disabled=${!state.connected}
            />
          </div>
          <div class="login-error" style="display:none"></div>
          <button
            type="submit"
            class="login-button"
            ?disabled=${!state.connected}
          >
            Sign In
          </button>
        </form>
      </div>
    </div>

    <style>
      .login-page {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 25%, #e0e7ff 50%, #c7d2fe 75%, #ddd6fe 100%);
        background-size: 400% 400%;
        animation: login-bg-shift 12s ease infinite;
        font-family: var(--font-sans, system-ui, -apple-system, sans-serif);
        position: relative;
        overflow: hidden;
      }
      /* Soft floating orbs for depth */
      .login-page::before,
      .login-page::after {
        content: "";
        position: absolute;
        border-radius: 50%;
        filter: blur(80px);
        opacity: 0.5;
        pointer-events: none;
      }
      .login-page::before {
        width: 500px;
        height: 500px;
        background: radial-gradient(circle, rgba(147, 197, 253, 0.7), transparent 70%);
        top: -120px;
        right: -100px;
        animation: login-orb-float 8s ease-in-out infinite alternate;
      }
      .login-page::after {
        width: 400px;
        height: 400px;
        background: radial-gradient(circle, rgba(196, 181, 253, 0.6), transparent 70%);
        bottom: -80px;
        left: -60px;
        animation: login-orb-float 10s ease-in-out infinite alternate-reverse;
      }
      @keyframes login-bg-shift {
        0%, 100% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
      }
      @keyframes login-orb-float {
        0% { transform: translate(0, 0) scale(1); }
        100% { transform: translate(30px, -20px) scale(1.08); }
      }

      .login-card {
        position: relative;
        z-index: 1;
        width: 100%;
        max-width: 420px;
        padding: 2.5rem 2.5rem 2rem;
        border-radius: 24px;
        /* Liquid glass: translucent white with strong blur */
        background: rgba(255, 255, 255, 0.35);
        backdrop-filter: blur(24px) saturate(1.4);
        -webkit-backdrop-filter: blur(24px) saturate(1.4);
        border: 1px solid rgba(255, 255, 255, 0.5);
        box-shadow:
          0 8px 32px rgba(100, 130, 200, 0.18),
          0 1.5px 0 rgba(255, 255, 255, 0.6) inset,
          0 -1px 3px rgba(100, 130, 200, 0.08) inset;
      }
      .login-header {
        text-align: center;
        margin-bottom: 1.75rem;
      }
      .login-logo {
        margin-bottom: 1rem;
      }
      .login-logo img {
        width: 140px;
        height: 140px;
        border-radius: 28px;
        filter: drop-shadow(0 6px 20px rgba(99, 102, 241, 0.25));
        background: rgba(255, 255, 255, 0.6);
        padding: 4px;
      }
      .login-title {
        margin: 0;
        font-size: 1.75rem;
        font-weight: 700;
        color: #1e293b;
        letter-spacing: -0.01em;
      }
      .login-subtitle {
        margin: 0.35rem 0 0;
        font-size: 0.9rem;
        color: #64748b;
        font-weight: 500;
      }
      .login-connecting {
        text-align: center;
        padding: 0.5rem 0.75rem;
        margin-bottom: 1rem;
        border-radius: 10px;
        background: rgba(251, 191, 36, 0.15);
        border: 1px solid rgba(251, 191, 36, 0.3);
        color: #92400e;
        font-size: 0.8rem;
        backdrop-filter: blur(8px);
      }
      .login-form {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
      .login-field {
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
      }
      .login-field label {
        font-size: 0.8rem;
        font-weight: 600;
        color: #475569;
        letter-spacing: 0.02em;
      }
      .login-field input {
        padding: 0.7rem 0.85rem;
        border-radius: 12px;
        border: 1px solid rgba(148, 163, 184, 0.4);
        background: rgba(255, 255, 255, 0.5);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        color: #1e293b;
        font-size: 0.9rem;
        outline: none;
        transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
      }
      .login-field input::placeholder {
        color: #94a3b8;
      }
      .login-field input:focus {
        border-color: rgba(99, 102, 241, 0.5);
        background: rgba(255, 255, 255, 0.7);
        box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12), 0 2px 8px rgba(99, 102, 241, 0.08);
      }
      .login-field input:disabled {
        opacity: 0.5;
      }
      .login-error {
        padding: 0.5rem 0.75rem;
        border-radius: 10px;
        background: rgba(239, 68, 68, 0.1);
        color: #b91c1c;
        font-size: 0.8rem;
        border: 1px solid rgba(239, 68, 68, 0.25);
        backdrop-filter: blur(8px);
      }
      .login-button {
        padding: 0.75rem;
        border-radius: 14px;
        border: none;
        background: linear-gradient(135deg, #818cf8 0%, #6366f1 50%, #7c3aed 100%);
        color: #fff;
        font-size: 0.95rem;
        font-weight: 600;
        cursor: pointer;
        transition: transform 0.15s, box-shadow 0.2s, opacity 0.15s;
        margin-top: 0.35rem;
        box-shadow:
          0 4px 14px rgba(99, 102, 241, 0.35),
          0 1px 0 rgba(255, 255, 255, 0.2) inset;
        letter-spacing: 0.01em;
      }
      .login-button:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow:
          0 6px 20px rgba(99, 102, 241, 0.45),
          0 1px 0 rgba(255, 255, 255, 0.25) inset;
      }
      .login-button:active:not(:disabled) {
        transform: translateY(0);
        box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
      }
      .login-button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    </style>
  `;
}
